/* eslint-disable no-template-curly-in-string */
/* eslint-disable react/button-has-type */
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { IAlertProps } from '../../interface/IAlertProps';
import { getDefaultAlertProps } from '../../utils/utils';
import { IApiResult } from '../../interface/IApiResult';
import { API_STATUS_PROBLEM } from '../../constant/constants';
import { AlertSeverityEnum } from '../../enum/AlertSeverityEnum';
import { MyAlert } from '../UI/MyAlert';
import { ContentDto } from '../../dto/ContentDto';
import { TextDto } from '../../dto/TextDto';
import { BackDropDiv } from '../backdrop/BackDropDiv';
import styles from './Dialog.module.css';
import { AtlasNewDto } from '../../dto/AtlasNewDto';
import { apiGetAtlas3DNew } from '../../service/apiGetAtlas3DNew';
import { apiGetFullBodyDescription } from '../../service/apiGetFullBodyDescription';
import { apiSaveFullBodyDescription } from '../../service/apiSaveFullBodyDescription';

interface Props {
  open: boolean;
  isEdit?: boolean;
  onClose: () => void;
  dto?: ContentDto;
}

export const DialogFullBodyDescription: FunctionComponent<Props> = ({
  open,
  dto,
  isEdit,
  onClose,
}) => {
  const [markdown, setMarkdown] = useState<string>('');
  const [preview, setPreview] = useState<string>('');
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const previewRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollPositionRef = useRef<{ textarea: number; preview: number }>({
    textarea: 0,
    preview: 0,
  });
  const [atlas, setAtlas] = useState<AtlasNewDto[]>([]);
  // Сохраняем позицию прокрутки перед любыми изменениями
  const saveScrollPosition = () => {
    if (textareaRef.current) {
      scrollPositionRef.current.textarea = textareaRef.current.scrollTop;
    }
    if (previewRef.current) {
      scrollPositionRef.current.preview = previewRef.current.scrollTop;
    }
  };

  const getIdArray = (array: any, typeFind: number) => {
    const arrId = [];
    for (let i = 0; i < array.length; i += 1) {
      arrId.push(array[i].atlasId);
    }
    return arrId;
  };

  const getNameArray = (array: any, typeFind: number) => {
    const arrName = [];
    for (let i = 0; i < array.length; i += 1) {
      if (typeFind === 0) {
        arrName.push(array[i].latSmew.replaceAll('_', '"'));
      }
      if (typeFind === 1) {
        arrName.push(array[i].latPr.replaceAll('_', '"'));
      }
      if (typeFind === 2) {
        arrName.push(array[i].latSech.replaceAll('_', '"'));
      }
      if (typeFind === 3) {
        arrName.push(array[i].latGaivar.replaceAll('_', '"'));
      }
    }

    return arrName;
  };

  // Восстанавливаем позицию прокрутки после изменений
  const restoreScrollPosition = () => {
    setTimeout(() => {
      if (
        textareaRef.current &&
        scrollPositionRef.current.textarea !== undefined
      ) {
        textareaRef.current.scrollTop = scrollPositionRef.current.textarea;
      }
      if (
        previewRef.current &&
        scrollPositionRef.current.preview !== undefined
      ) {
        previewRef.current.scrollTop = scrollPositionRef.current.preview;
      }
    }, 10);
  };

  // Обработка изменений в markdown
  const handleMarkdownChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMarkdown = e.target.value;
    setMarkdown(newMarkdown);
    setPreview(newMarkdown);
  };

  // Обработка инлайн-разметки
  const processInlineMarkdown = (text: string): string =>
    text
      .replace(
        /\*\*(.*?)\*\*/g,
        '<strong class="${styles.previewStrong}">$1</strong>'
      )
      .replace(/\*(.*?)\*/g, '<em class="${styles.previewEm}">$1</em>')
      .replace(/`(.*?)`/g, '<code class="${styles.previewCode}">$1</code>')
      .replace(
        /!\[(.*?)\]\((.*?)\)/g,
        `<img alt="$1" src="$2" class="${styles.previewImage}" />`
      )
      .replace(
        /\[(.*?)\]\((http.*?)\)/g,
        `<a href="$2" target="_blank" class="${styles.previewLink}">$1</a>`
      );

  // Экранирование HTML
  const escapeHtml = (text: string): string => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  const renderMarkdown = (text: string): string => {
    if (!text) return '';

    const lines = text.split('\n');
    let inCodeBlock = false;
    let inList = false;
    let listTag = 'ul'; // храним тип списка

    const processedLines = lines.map((line, index) => {
      // Обработка блоков кода
      if (line.trim().startsWith('```')) {
        inCodeBlock = !inCodeBlock;
        return line;
      }

      if (inCodeBlock) {
        return `<div class="${styles.previewCodeLine}">${escapeHtml(
          line
        )}</div>`;
      }

      // Заголовки (только в начале строки)
      if (line.match(/^#{1,6}\s/)) {
        const level = line.match(/^(#{1,6})\s/)?.[1].length || 1;
        const content = line.replace(/^#{1,6}\s/, '');
        return `<h${level} class="${
          styles[`previewH${level}`]
        }">${content}</h${level}>`;
      }

      // Блоки цитат
      if (line.match(/^>\s/)) {
        const content = line.replace(/^>\s/, '');
        return `<blockquote class="${styles.previewBlockquote}">${content}</blockquote>`;
      }

      // Списки
      if (line.match(/^[-*+]\s/) || line.match(/^\d+\.\s/)) {
        const content = line.replace(/^[-*+]\s/, '').replace(/^\d+\.\s/, '');
        const tag = line.match(/^\d+\.\s/) ? 'ol' : 'ul';
        listTag = tag; // сохраняем тип списка
        let result = '';

        if (!inList) {
          inList = true;
          result = `<${tag} class="${styles.previewList}"><li class="${styles.previewListItem}">${content}</li>`;
        } else {
          result = `<li class="${styles.previewListItem}">${content}</li>`;
        }

        // Проверяем, следующая ли это последняя строка
        const nextLine = lines[index + 1];
        if (
          !nextLine ||
          (!nextLine.match(/^[-*+]\s/) && !nextLine.match(/^\d+\.\s/))
        ) {
          inList = false;
          result += `</${tag}>`;
        }

        return result;
      }

      // Если мы были в списке и текущая строка не элемент списка, закрываем список
      if (inList) {
        inList = false;
        return `</${listTag}><p class="${styles.previewParagraph}">${line}</p>`;
      }

      // Пустые строки
      if (line.trim() === '') {
        return '<br>';
      }

      // Обычный текст с инлайн-разметкой
      return `<p class="${styles.previewParagraph}">${processInlineMarkdown(
        line
      )}</p>`;
    });

    // Обрабатываем специальные ссылки и инлайн-разметку
    return processedLines
      .join('\n')
      .replace(
        /\[(.*?)\]\(anatatlas:\/\/(.*?)\)/gim,
        `<a href="anatatlas://$2" class="${styles.anatomyLink}" title="Открыть в анатомическом атласе">$1</a>`
      );
  };

  // Вставка markdown с сохранением позиции
  const insertMarkdown = (syntax: string, placeholder = '') => {
    if (!textareaRef.current) return;

    saveScrollPosition(); // Сохраняем позицию перед изменением

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = markdown.substring(start, end);

    let newText: string;
    if (selectedText) {
      newText =
        markdown.substring(0, start) +
        syntax.replace('{text}', selectedText) +
        markdown.substring(end);
    } else {
      newText =
        markdown.substring(0, start) +
        syntax.replace('{text}', placeholder) +
        markdown.substring(end);
    }

    setMarkdown(newText);
    setPreview(newText);

    /*
    // Восстанавливаем позицию и курсор
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();

        if (selectedText) {
          const newPosition =
            start + syntax.replace('{text}', selectedText).length;
          textareaRef.current.setSelectionRange(newPosition, newPosition);
        } else {
          const cursorPos =
            start +
            syntax.replace('{text}', placeholder).indexOf(placeholder) +
            placeholder.length;
          textareaRef.current.setSelectionRange(cursorPos, cursorPos);
        }
      }

      restoreScrollPosition(); // Восстанавливаем позицию прокрутки
    }, 10);
    */
  };

  // Отмена изменений (шаг назад)
  const undoChange = () => {
    if (historyIndex > 0) {
      saveScrollPosition(); // Сохраняем позицию перед отменой
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setMarkdown(history[newIndex]);
      setPreview(history[newIndex]);
      restoreScrollPosition(); // Восстанавливаем позицию после отмены
    }
  };

  const insertAnatomyLink = () => {
    if (!textareaRef.current) return;

    saveScrollPosition(); // Сохраняем позицию перед изменением

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = markdown.substring(start, end);

    let linkTextSmew = '';
    let linkTextLatSmew = '';
    let linkTextPr = '';
    let linkTextLatPr = '';
    let linkTextSech = '';
    let linkTextLatSech = '';
    let linkTextGaivar = '';
    let linkTextLatGaivar = '';

    let anatomyId = '';

    for (let i = 0; i < atlas.length; i += 1) {
      linkTextSmew = `${atlas[i].rus}`;
      linkTextLatSmew = `${atlas[i].latSmew}`;

      linkTextPr = `${atlas[i].rusPr}`;
      linkTextLatPr = `${atlas[i].latPr}`;

      linkTextSech = `${atlas[i].rusSech}`;
      linkTextLatSech = `${atlas[i].latSech}`;

      linkTextGaivar = `${atlas[i].rusGaivar}`;
      linkTextLatGaivar = `${atlas[i].latGaivar}`;

      if (linkTextLatSmew !== '') {
        linkTextLatSmew = ` /Т: ${linkTextLatSmew}`;
      }
      if (linkTextLatPr !== '') {
        linkTextLatPr = ` /П: ${linkTextLatPr}`;
      }
      if (linkTextLatSech !== '') {
        linkTextLatSech = ` /C: ${linkTextLatSech}`;
      }
      if (linkTextLatGaivar !== '') {
        linkTextLatGaivar = ` /Г: ${linkTextLatGaivar}`;
      }
      const item = `**ID: ${atlas[i].atlasId}** *(${linkTextLatSmew}${linkTextLatPr}${linkTextLatSech}${linkTextLatGaivar})*`;
      if (!markdown.includes(item)) {
        anatomyId = `${anatomyId}${item}\n\n\n\n`;
      }
    }

    const newText = `${markdown}${anatomyId}`;
    setMarkdown(newText);
    setPreview(newText);

    /*
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const cursorPos = start + anatomyLink.length;
        textareaRef.current.setSelectionRange(cursorPos, cursorPos);
      }

      restoreScrollPosition(); // Восстанавливаем позицию прокрутки
    }, 10);
    */
  };

  const clearEditor = () => {
    saveScrollPosition(); // Сохраняем позицию перед очисткой
    setMarkdown('');
    setPreview('');
    restoreScrollPosition(); // Восстанавливаем позицию после очистки
  };

  const [alertProps, setAlertProps] = useState<IAlertProps>(
    getDefaultAlertProps()
  );

  const [abtrsact, setAbstract] = useState('');
  const [backDrop, setBackDrop] = useState(false);
  const handleClose = () => {
    onClose();
  };
  const { section } = useParams();

  const getAtlas3D = async (route: string, key: string) => {
    setBackDrop(true);
    const apiResult: IApiResult = await apiGetAtlas3DNew(route, key);
    if (apiResult.isSuccess) {
      setAtlas(apiResult.data);
    } else if (apiResult.status !== 404) {
      setAlertProps({
        message: API_STATUS_PROBLEM,
        severity: AlertSeverityEnum.error,
      });
    }
    setBackDrop(false);
  };

  const getFullBodyDsecription = async (route: string, key: string) => {
    setBackDrop(true);
    const apiResult: IApiResult = await apiGetFullBodyDescription(route, key);
    if (apiResult.isSuccess) {
      setAbstract(apiResult.data);
      setMarkdown(apiResult.data);
      setPreview(apiResult.data);
      setHistory([apiResult.data]);
      setHistoryIndex(0);
    } else if (apiResult.status !== 404) {
      setAlertProps({
        message: API_STATUS_PROBLEM,
        severity: AlertSeverityEnum.error,
      });
    }
    setBackDrop(false);
  };

  const saveText = async (route: string, key: string) => {
    const textDto: TextDto = {
      text: markdown,
    };
    const apiResult: IApiResult = await apiSaveFullBodyDescription(
      route,
      key,
      textDto
    );
    if (apiResult.isSuccess) {
      setAlertProps({
        message: 'Данные записаны',
        severity: AlertSeverityEnum.info,
      });
      getFullBodyDsecription(section ?? '', dto?.key ?? '');
    } else {
      setAlertProps({
        message: API_STATUS_PROBLEM,
        severity: AlertSeverityEnum.error,
      });
    }
  };

  useEffect(() => {
    if (open) {
      getAtlas3D(section ?? '', dto?.key ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) {
      getFullBodyDsecription(section ?? '', dto?.key ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (historyIndex === -1) return;

    const newHistory = history.slice(0, historyIndex + 1);
    if (newHistory[newHistory.length - 1] !== markdown) {
      const updatedHistory = [...newHistory, markdown];
      setHistory(updatedHistory);
      setHistoryIndex(updatedHistory.length - 1);
    }
  }, [markdown]);

  return (
    <div>
      <Dialog
        open={open}
        fullWidth
        maxWidth="xl"
        style={{ userSelect: 'none' }}
        PaperProps={{
          sx: {
            height: '100%',
            backgroundColor: '#e7ebee',
            maxWidth: '100%',
            maxHeight: '100%',
          },
        }}
      >
        <BackDropDiv open={backDrop} />
        <MyAlert
          message={alertProps.message}
          severity={alertProps.severity}
          onClose={() => setAlertProps({ ...alertProps, message: '' })}
        />
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              style={{
                fontFamily: 'sans-serif',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '24px',
                color: '#000000',
                letterSpacing: 1,
              }}
              component="text"
            >
              {`Описание ID - ${dto?.key} ${dto?.title}`}
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton title="Закрыть" size="small" onClick={handleClose}>
                <CloseIcon fontSize="large" sx={{ color: '#000000' }} />
              </IconButton>
            </Stack>
          </Stack>
          <header className={styles.editorHeader}>
            <div className={styles.toolbar}>
              <button
                className={styles.toolbarButton}
                onClick={undoChange}
                title="Отменить (Ctrl+Z)"
                disabled={historyIndex <= 0}
              >
                ↩️
              </button>
              <button
                className={styles.toolbarButton}
                onClick={() => saveText(section ?? '', dto?.key ?? '')}
                title="Сохранить"
              >
                💾
              </button>
              <button
                className={styles.toolbarButton}
                onClick={() => insertMarkdown('# {text}', 'Заголовок')}
                title="Заголовок"
              >
                H1
              </button>
              <button
                className={styles.toolbarButton}
                onClick={() => insertMarkdown('## {text}', 'Подзаголовок')}
                title="Подзаголовок"
              >
                H2
              </button>
              <button
                className={styles.toolbarButton}
                onClick={() => insertMarkdown('**{text}**', 'Жирный текст')}
                title="Жирный текст"
              >
                <strong>B</strong>
              </button>
              <button
                className={styles.toolbarButton}
                onClick={() => insertMarkdown('*{text}*', 'Курсив')}
                title="Курсив"
              >
                <em>I</em>
              </button>
              <Stack direction="row" spacing={1}>
                <button
                  className={styles.toolbarButton}
                  onClick={insertAnatomyLink}
                  title="Cсылка"
                >
                  🔗
                </button>
              </Stack>
            </div>
          </header>
        </DialogTitle>
        <DialogContent>
          <div className={styles.markdownEditor}>
            <div className={styles.editorContainer}>
              <div className={styles.editorContent}>
                <div className={`${styles.editorPanel} ${styles.splitPanel}`}>
                  <div className={styles.panelHeader}>
                    <h2 className={styles.panelTitle}>Редактор Markdown</h2>
                  </div>
                  <textarea
                    ref={textareaRef}
                    id="markdown-textarea"
                    value={markdown}
                    onChange={handleMarkdownChange}
                    placeholder="Начните писать markdown здесь..."
                    className={styles.markdownInput}
                  />
                </div>

                <div className={`${styles.previewPanel} ${styles.splitPanel}`}>
                  <div className={styles.panelHeader}>
                    <h2 className={styles.panelTitle}>
                      Предварительный просмотр
                    </h2>
                  </div>
                  <div
                    ref={previewRef}
                    className={styles.markdownPreview}
                    dangerouslySetInnerHTML={{
                      __html: renderMarkdown(markdown),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

DialogFullBodyDescription.defaultProps = {
  isEdit: false,
  dto: undefined,
};
